import set from 'lodash/set';
import { TICKET_TYPE_STANDALONE } from 'common/entities/ticketType/selectors';
import { ENTITIES_KEY_FORM } from '../index';
import * as actionTypes from './actionTypes';
import { getTicketStyles, getTicketStylesByCategory, getTicketStyleCategories } from './selectors';
import { SPACING_DATA_MODE, SPACING_DATA_NODES_SEPARATION, SPACING_DATA_LABEL_SEPARATION } from './constants';

// (String, Int, Object) => Object
const setStyleConfigurationProp = (type, formId, styleConfiguration) => ({
    type,
    payload: {
        entities: {
            [ENTITIES_KEY_FORM]: {
                [formId]: {
                    styleConfiguration,
                },
            },
        },
    },
});

// (String, Array, String);
export const setStyleConfigurationFromPath = (formId, path, value) =>
    setStyleConfigurationProp('entities/form/styleConfiguration', formId, set({}, path, value));

// PATHS
// This paths can be used with setStyleConfigurationFromPath() and getFormStyleProperty()
// to avoid boilerplate creating more actions and selectors,
// improving readability and maintainability
const PATH_FORM = ['form'];
export const PATH_SPACING = [...PATH_FORM, 'spacing'];
export const PATH_SPACING_MODE = [...PATH_SPACING, SPACING_DATA_MODE];
export const PATH_SPACING_LABEL_SEPARATION = [...PATH_SPACING, SPACING_DATA_LABEL_SEPARATION];
export const PATH_SPACING_DATA_NODES_SEPARATION = [...PATH_SPACING, SPACING_DATA_NODES_SEPARATION];

// Form Colors
// (String, Object) => Object
const setFormColors = (formId, colors, type = actionTypes.SET_FORM_COLORS) =>
    setStyleConfigurationProp(type, formId, {
        form: {
            colors,
        },
    });

// (String, Int) => Object
export const setFormColorBackground = (formId, background) =>
    setFormColors(
        formId,
        {
            background,
        },
        actionTypes.SET_FORM_COLOR_BACKGROUND,
    );

// Form Background Image
// (String, Object) => Object
const setFormBackgroundImageStyle = (formId, backgroundImage, type = actionTypes.SET_FORM_BACKGROUND_IMAGE) =>
    setStyleConfigurationProp(type, formId, {
        form: {
            backgroundImage,
        },
    });

// (String, Str) => Object
export const setFormBackgroundImageBaseUrl = (formId, baseUrl) =>
    setFormBackgroundImageStyle(
        formId,
        {
            baseUrl,
        },
        actionTypes.SET_FORM_BACKGROUND_IMAGE_BASE_URL,
    );

// (String, Str) => Object
export const setFormBackgroundImageUrl = (formId, url) =>
    setFormBackgroundImageStyle(
        formId,
        {
            url,
        },
        actionTypes.SET_FORM_BACKGROUND_IMAGE_URL,
    );

// Form Background Image
// (String, Object) => Object
const setFormDisplayStyles = (formId, displayStyles, type = actionTypes.SET_FORM_DISPLAY_STYLES) =>
    setStyleConfigurationProp(type, formId, {
        form: {
            displayStyles,
        },
    });

// (String, Str) => Object
export const setFormDisplayMode = (formId, displayMode) =>
    setFormDisplayStyles(
        formId,
        {
            displayMode,
        },
        actionTypes.SET_FORM_DISPLAY_MODE,
    );

// (String, Str) => Object
export const setFormSpacing = (formId, displayMode) =>
    setFormDisplayStyles(
        formId,
        {
            displayMode,
        },
        actionTypes.SET_FORM_DISPLAY_MODE,
    );

// (String, Str) => Object
export const setFormPanelDisplay = (formId, panelDisplay) =>
    setFormDisplayStyles(
        formId,
        {
            panelDisplay,
        },
        actionTypes.SET_FORM_PANEL_DISPLAY,
    );

// (String, Int) => Object
export const setFormColorText = (formId, text) =>
    setFormColors(
        formId,
        {
            text,
        },
        actionTypes.SET_FORM_COLOR_TEXT,
    );

// (String, Int) => Object
export const setFormColorInput = (formId, input) =>
    setFormColors(
        formId,
        {
            input,
        },
        actionTypes.SET_FORM_COLOR_INPUT,
    );

// (String, Int) => Object
export const setFormColorAccent = (formId, accent) =>
    setFormColors(
        formId,
        {
            accent,
        },
        actionTypes.SET_FORM_COLOR_ACCENT,
    );

// Form Fonts
// (String, Object) => Object
const setFormFonts = (formId, fonts, type = actionTypes.SET_FORM_FONTS) =>
    setStyleConfigurationProp(type, formId, {
        form: {
            fonts,
        },
    });

// (String, Int) => Object
export const setFormFontPrimary = (formId, primary) =>
    setFormFonts(
        formId,
        {
            primary,
        },
        actionTypes.SET_FORM_FONT_PRIMARY,
    );

// (String, Int) => Object
export const setFormFontSecondary = (formId, secondary) =>
    setFormFonts(
        formId,
        {
            secondary,
        },
        actionTypes.SET_FORM_FONT_SECONDARY,
    );

// Form Question Text
// (String, Object) => Object
const setFormFieldsQuestion = (formId, questionText, type = actionTypes.SET_FORM_FIELDS_QUESTION) =>
    setStyleConfigurationProp(type, formId, {
        formFields: {
            questionText,
        },
    });

// (String, Int) => Object
export const setFormFieldsQuestionFont = (formId, font) =>
    setFormFieldsQuestion(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_FIELDS_QUESTION_FONT,
    );

// (String, Int) => Object
export const setFormFieldsQuestionFontSize = (formId, fontSize) =>
    setFormFieldsQuestion(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_FIELDS_QUESTION_FONTSIZE,
    );

// (String, Int) => Object
export const setFormFieldsQuestionColor = (formId, color) =>
    setFormFieldsQuestion(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_FIELDS_QUESTION_COLOR,
    );

// Form Fields Input
// (String, Object) => Object
const setFormFieldsInput = (formId, inputText, type = actionTypes.SET_FORM_FIELDS_INPUT) =>
    setStyleConfigurationProp(type, formId, {
        formFields: {
            inputText,
        },
    });

// (String, Int) => Object
export const setFormFieldsInputFont = (formId, font) =>
    setFormFieldsInput(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_FIELDS_INPUT_FONT,
    );

// (String, Int) => Object
export const setFormFieldsInputFontSize = (formId, fontSize) =>
    setFormFieldsInput(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_FIELDS_INPUT_FONTSIZE,
    );

// (String, Int) => Object
export const setFormFieldsInputAnswerColor = (formId, color) =>
    setFormFieldsInput(
        formId,
        {
            colors: {
                answer: color,
            },
        },
        actionTypes.SET_FORM_FIELDS_INPUT_ANSWER_COLOR,
    );

// (String, Int) => Object
export const setFormFieldsInputPlaceholderColor = (formId, color) =>
    setFormFieldsInput(
        formId,
        {
            colors: { placeholder: color },
        },
        actionTypes.SET_FORM_FIELDS_INPUT_PLACEHOLDER_COLOR,
    );

// Form Fields Input
// (Int, Object) => Object
const setFormHelperText = (formId, helperText, type = actionTypes.SET_FORM_HELPER_TEXT) =>
    setStyleConfigurationProp(type, formId, {
        formFields: {
            helperText,
        },
    });

// (Int, Int) => Object
export const setFormInputHelperTextFont = (formId, font) =>
    setFormHelperText(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_FIELDS_HELPER_TEXT_FONT,
    );

// (Int, Int) => Object
export const setFormInputHelperTextFontSize = (formId, fontSize) =>
    setFormHelperText(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_FIELDS_HELPER_TEXT_FONTSIZE,
    );

// (Int, Int) => Object
export const setFormInputHelperTextColor = (formId, color) =>
    setFormHelperText(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_FIELDS_HELPER_TEXT_COLOR,
    );

// Form Fields Border
// (String, Object) => Object
const setFormFieldsBorder = (formId, border, type = actionTypes.SET_FORM_FIELDS_BORDER) =>
    setStyleConfigurationProp(type, formId, {
        formFields: {
            border,
        },
    });

// (String, Int) => Object
export const setFormFieldsBorderRadius = (formId, radius) =>
    setFormFieldsBorder(
        formId,
        {
            radius,
        },
        actionTypes.SET_FORM_FIELDS_BORDER_RADIUS,
    );

// (String, Object) => Object
export const setFormFieldsBorderSizes = (formId, sizes) =>
    setFormFieldsBorder(
        formId,
        {
            sizes,
        },
        actionTypes.SET_FORM_FIELDS_BORDER_SIZES,
    );

// (String, Int) => Object
export const setFormFieldsBorderSizeTop = (formId, top) =>
    setFormFieldsBorder(
        formId,
        {
            sizes: {
                top,
            },
        },
        actionTypes.SET_FORM_FIELDS_BORDER_SIZE_TOP,
    );

// (String, Int) => Object
export const setFormFieldsBorderSizeBottom = (formId, bottom) =>
    setFormFieldsBorder(
        formId,
        {
            sizes: {
                bottom,
            },
        },
        actionTypes.SET_FORM_FIELDS_BORDER_SIZE_BOTTOM,
    );

// (String, Int) => Object
export const setFormFieldsBorderSizeLeft = (formId, left) =>
    setFormFieldsBorder(
        formId,
        {
            sizes: {
                left,
            },
        },
        actionTypes.SET_FORM_FIELDS_BORDER_SIZE_LEFT,
    );

// (String, Int) => Object
export const setFormFieldsBorderSizeRight = (formId, right) =>
    setFormFieldsBorder(
        formId,
        {
            sizes: {
                right,
            },
        },
        actionTypes.SET_FORM_FIELDS_BORDER_SIZE_RIGHT,
    );

// (String, Int) => Object
export const setFormFieldsBorderWeight = (formId, weight) => (dispatch) => {
    dispatch(setFormFieldsBorderSizeTop(formId, weight));
    dispatch(setFormFieldsBorderSizeBottom(formId, weight));
    dispatch(setFormFieldsBorderSizeLeft(formId, weight));
    dispatch(setFormFieldsBorderSizeRight(formId, weight));
};

// (String, Object) => Object
export const setFormFieldsBorderColors = (formId, colors, type = actionTypes.SET_FORM_FIELDS_BORDER_COLORS) =>
    setFormFieldsBorder(
        formId,
        {
            colors,
        },
        type,
    );

// (String, Int) => Object
export const setFormFieldsBorderColorDefault = (formId, defaultColor) =>
    setFormFieldsBorderColors(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_FIELDS_BORDER_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormFieldsBorderColorHover = (formId, hover) =>
    setFormFieldsBorderColors(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_FIELDS_BORDER_COLOR_HOVER,
    );

export const setFormFieldsBorderColorFocus = (formId, focus) =>
    setFormFieldsBorderColors(
        formId,
        {
            focus,
        },
        actionTypes.SET_FORM_FIELDS_BORDER_COLOR_FOCUS,
    );

// Form Fields Input Background Colors
// (String, Object) => Object
const setFormFieldsInputBackgroundColors = (
    formId,
    inputBackgroundColors,
    type = actionTypes.SET_FORM_FIELDS_INPUT_BACKGROUND,
) =>
    setStyleConfigurationProp(type, formId, {
        formFields: {
            inputBackgroundColors,
        },
    });

// (String, Int) => Object
export const setFormFieldsInputBackgroundColorDefault = (formId, defaultColor) =>
    setFormFieldsInputBackgroundColors(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_FIELDS_INPUT_BACKGROUND_DEFAULT,
    );

// (String, Int) => Object
export const setFormFieldsInputBackgroundColorHover = (formId, hover) =>
    setFormFieldsInputBackgroundColors(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_FIELDS_INPUT_BACKGROUND_HOVER,
    );

// Button colors
// (String, Object) => Object
const setFormButtonColors = (formId, colors, type = actionTypes.SET_FORM_BUTTON_COLORS) =>
    setStyleConfigurationProp(type, formId, {
        button: {
            colors,
        },
    });

// (String, Int) => Object
export const setFormButtonColorDefault = (formId, defaultColor) =>
    setFormButtonColors(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_BUTTON_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormButtonColorHover = (formId, hover) =>
    setFormButtonColors(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_BUTTON_COLOR_HOVER,
    );

// Button colors
// (String, Object) => Object
const setFormButtonText = (formId, text, type = actionTypes.SET_FORM_BUTTON_TEXT) =>
    setStyleConfigurationProp(type, formId, {
        button: {
            text,
        },
    });

// (String, Int) => Object
export const setFormButtonTextFont = (formId, font) =>
    setFormButtonText(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_BUTTON_TEXT_FONT,
    );

// (String, Int) => Object
export const setFormButtonTextFontSize = (formId, fontSize) =>
    setFormButtonText(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_BUTTON_TEXT_FONTSIZE,
    );

// (String, Object) => Object
const setFormButtonTextColors = (formId, colors, type = actionTypes.SET_FORM_BUTTON_TEXT_COLORS) =>
    setFormButtonText(
        formId,
        {
            colors,
        },
        type,
    );

// (String, Int) => Object
export const setFormButtonTextColorDefault = (formId, defaultColor) =>
    setFormButtonTextColors(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_BUTTON_TEXT_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormButtonTextColorHover = (formId, hover) =>
    setFormButtonTextColors(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_BUTTON_TEXT_COLOR_HOVER,
    );

// Button border
// (String, Object) => Object
const setFormButtonBorder = (formId, border, type = actionTypes.SET_FORM_BUTTON_BORDER) =>
    setStyleConfigurationProp(type, formId, {
        button: {
            border,
        },
    });

// (String, Int) => Object
export const setFormButtonBorderRadius = (formId, radius) =>
    setFormButtonBorder(
        formId,
        {
            radius,
        },
        actionTypes.SET_FORM_BUTTON_BORDER_RADIUS,
    );

// (String, Object) => Object
export const setFormButtonBorderSizes = (formId, sizes, type = actionTypes.SET_FORM_BUTTON_BORDER_SIZES) =>
    setFormButtonBorder(
        formId,
        {
            sizes,
        },
        type,
    );

// (String, Int) => Object
export const setFormButtonBorderSizeTop = (formId, top) =>
    setFormButtonBorderSizes(
        formId,
        {
            top,
        },
        actionTypes.SET_FORM_BUTTON_BORDER_SIZE_TOP,
    );

// (String, Int) => Object
export const setFormButtonBorderSizeBottom = (formId, bottom) =>
    setFormButtonBorderSizes(
        formId,
        {
            bottom,
        },
        actionTypes.SET_FORM_BUTTON_BORDER_SIZE_BOTTOM,
    );

// (String, Int) => Object
export const setFormButtonBorderSizeRight = (formId, right) =>
    setFormButtonBorderSizes(
        formId,
        {
            right,
        },
        actionTypes.SET_FORM_BUTTON_BORDER_SIZE_RIGHT,
    );

// (String, Int) => Object
export const setFormButtonBorderSizeLeft = (formId, left) =>
    setFormButtonBorderSizes(
        formId,
        {
            left,
        },
        actionTypes.SET_FORM_BUTTON_BORDER_SIZE_LEFT,
    );

// (String, Int) => Thunk
export const setAllFormButtonBorderSizes = (formId, size) => (dispatch) => {
    dispatch(setFormButtonBorderSizeTop(formId, size));
    dispatch(setFormButtonBorderSizeBottom(formId, size));
    dispatch(setFormButtonBorderSizeRight(formId, size));
    dispatch(setFormButtonBorderSizeLeft(formId, size));
};

// (String, Object) => Object
export const setFormButtonBorderColors = (formId, colors, type = actionTypes.SET_FORM_BUTTON_BORDER_COLORS) =>
    setFormButtonBorder(
        formId,
        {
            colors,
        },
        type,
    );

// (String, Int) => Object
export const setFormButtonBorderColorDefault = (formId, defaultColor) =>
    setFormButtonBorderColors(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_BUTTON_BORDER_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormButtonBorderColorHover = (formId, hover) =>
    setFormButtonBorderColors(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_BUTTON_BORDER_COLOR_HOVER,
    );

export const setFormButtonBorderColorFocus = (formId, focus) =>
    setFormButtonBorderColors(
        formId,
        {
            focus,
        },
        actionTypes.SET_FORM_BUTTON_BORDER_COLOR_FOCUS,
    );

const setFormButtonPosition = (formId, position, type = actionTypes.SET_FORM_BUTTON_POSITION) =>
    setStyleConfigurationProp(type, formId, {
        button: position,
    });

// (String, Str) => Object
export const setFormSubmitButtonPosition = (formId, position) =>
    setFormButtonPosition(
        formId,
        {
            position,
        },
        actionTypes.SET_FORM_SUBMIT_BUTTON_POSITION,
    );

// Headline Text
// (String, Object) => Object
const setFormHeadLineText = (formId, headLineText, type = actionTypes.SET_FORM_HEADLINE_TEXT) =>
    setStyleConfigurationProp(type, formId, {
        headLineText,
    });

// (String, Int) => Object
export const setFormHeadLineTextFont = (formId, font) =>
    setFormHeadLineText(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_HEADLINE_FONT,
    );

// (String, Int) => Object
export const setFormHeadLineTextFontsize = (formId, fontSize) =>
    setFormHeadLineText(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_HEADLINE_FONTSIZE,
    );

// (String, Int) => Object
export const setFormHeadLineTextColor = (formId, color) =>
    setFormHeadLineText(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_HEADLINE_COLOR,
    );

// (String, Str) => Object
export const setFormHeadlinePosition = (formId, position) =>
    setFormHeadLineText(
        formId,
        {
            position,
        },
        actionTypes.SET_FORM_HEADLINE_POSITION,
    );

// Errors
// (String, Object) => Object
const setFormErrorsStyle = (formId, errors, type = actionTypes.SET_FORM_ERRORS_STYLE) =>
    setStyleConfigurationProp(type, formId, {
        errors,
    });

// (String, Int) => Object
export const setFormErrorsFont = (formId, font) =>
    setFormErrorsStyle(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_ERRORS_FONT,
    );

// (String, Int) => Object
export const setFormErrorsFontsize = (formId, fontSize) =>
    setFormErrorsStyle(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_ERRORS_FONTSIZE,
    );

// (String, Int) => Object
export const setFormErrorsColor = (formId, color) =>
    setFormErrorsStyle(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_ERRORS_COLOR,
    );

// Form Content
// (String, Object) => Object
const setFormContentTextStyle = (formId, formContent, type = actionTypes.SET_FORM_CONTENT_TEXT_STYLE) =>
    setStyleConfigurationProp(type, formId, {
        formContent,
    });

// (String, Int) => Object
export const setFormTextElementFont = (formId, font) =>
    setFormContentTextStyle(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_TEXT_ELEMENT_FONT,
    );

// (String, Int) => Object
export const setFormTextElementFontsize = (formId, fontSize) =>
    setFormContentTextStyle(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_TEXT_ELEMENT_FONTSIZE,
    );

// (String, Int) => Object
export const setFormTextElementColor = (formId, color) =>
    setFormContentTextStyle(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_TEXT_ELEMENT_COLOR,
    );

// (String, String) => Object
export const setFormTextElementPosition = (formId, position) =>
    setFormContentTextStyle(
        formId,
        {
            position,
        },
        actionTypes.SET_FORM_TEXT_ELEMENT_POSITION,
    );

// Form Header Image
// (String, Object) => Object
const setFormHeaderImageStyle = (formId, headerImage, type = actionTypes.SET_FORM_HEADER_IMAGE_STYLE) =>
    setStyleConfigurationProp(type, formId, {
        headerImage,
    });

// (String, Str) => Object
export const setFormHeaderImageSize = (formId, size) =>
    setFormHeaderImageStyle(
        formId,
        {
            size,
        },
        actionTypes.SET_FORM_HEADER_IMAGE_SIZE,
    );

// (String, Str) => Object
export const setFormHeaderImageType = (formId, type) =>
    setFormHeaderImageStyle(
        formId,
        {
            type,
        },
        actionTypes.SET_FORM_HEADER_IMAGE_TYPE,
    );

// (String, Str) => Object
export const setFormHeaderImageBaseUrl = (formId, baseUrl) =>
    setFormHeaderImageStyle(
        formId,
        {
            baseUrl,
        },
        actionTypes.SET_FORM_HEADER_IMAGE_BASE_URL,
    );

// (String, Str) => Object
export const setFormHeaderImageUrl = (formId, url) =>
    setFormHeaderImageStyle(
        formId,
        {
            url,
        },
        actionTypes.SET_FORM_HEADER_IMAGE_URL,
    );

// (String, Str) => Object
export const setFormHeaderImageAltText = (formId, altText) =>
    setFormHeaderImageStyle(
        formId,
        {
            altText,
        },
        actionTypes.SET_FORM_HEADER_IMAGE_ALT_TEXT,
    );

// (String, Str) => Object
export const setFormHeaderImagePosition = (formId, position) =>
    setFormHeaderImageStyle(
        formId,
        {
            position,
        },
        actionTypes.SET_FORM_HEADER_IMAGE_POSITION,
    );

// Close Button
// (String, Object) => Object
const setFormCloseButton = (formId, closeButton, type = actionTypes.SET_FORM_CLOSE_BUTTON) =>
    setStyleConfigurationProp(type, formId, {
        closeButton,
    });

// (String, Int) => Object
export const setFormCloseButtonColorDefault = (formId, defaultColor) =>
    setFormCloseButton(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_CLOSE_BUTTON_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormCloseButtonColorHover = (formId, hover) =>
    setFormCloseButton(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_CLOSE_BUTTON_COLOR_HOVER,
    );

export const setFormCloseButtonColorFocus = (formId, focus) =>
    setFormCloseButton(
        formId,
        {
            focus,
        },
        actionTypes.SET_FORM_CLOSE_BUTTON_COLOR_FOCUS,
    );
// Link Text
// (String, Object) => Object
const setFormLinks = (formId, links, type = actionTypes.SET_FORM_LINKS) =>
    setStyleConfigurationProp(type, formId, {
        form: {
            links,
        },
    });

// (String, Int) => Object
export const setFormLinksColorDefault = (formId, defaultColor) =>
    setFormLinks(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_LINKS_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormLinksColorHover = (formId, hover) =>
    setFormLinks(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_LINKS_COLOR_HOVER,
    );

export const setFormLinksColorFocus = (formId, focus) =>
    setFormLinks(
        formId,
        {
            focus,
        },
        actionTypes.SET_FORM_LINKS_COLOR_FOCUS,
    );

// Form Session Stepper
// (String, Object) => Object
const setFormStepper = (formId, stepper, type = actionTypes.SET_FORM_SESSION_STEPPER) =>
    setStyleConfigurationProp(type, formId, {
        stepper,
    });

// (String, Int) => Object
export const setFormStepperFont = (formId, font) =>
    setFormStepper(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_SESSION_STEPPER_FONT,
    );

// (String, Int) => Object
export const setFormStepperFontSize = (formId, fontSize) =>
    setFormStepper(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_SESSION_STEPPER_FONT_SIZE,
    );

// (String, Int) => Object
export const setFormStepperPrimaryColor = (formId, primaryColor) =>
    setFormStepper(
        formId,
        {
            primaryColor,
        },
        actionTypes.SET_FORM_SESSION_STEPPER_PRIMARY_COLOR,
    );

// (String, Int) => Object
export const setFormStepperSecondaryColor = (formId, secondaryColor) =>
    setFormStepper(
        formId,
        {
            secondaryColor,
        },
        actionTypes.SET_FORM_SESSION_STEPPER_SECONDARY_COLOR,
    );

// Form Session Title
// (String, Object) => Object
const setFormSessionTitleStyle = (formId, title, type = actionTypes.SET_FORM_SESSION_TITLE) =>
    setStyleConfigurationProp(type, formId, {
        sessionDetails: {
            title,
        },
    });

// (String, Str) => Object
export const setFormSessionTitleFontSize = (formId, fontSize) =>
    setFormSessionTitleStyle(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_SESSION_TITLE_FONTSIZE,
    );

// (String, Str) => Object
export const setFormSessionTitleFont = (formId, font) =>
    setFormSessionTitleStyle(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_SESSION_TITLE_FONT,
    );

// (String, Str) => Object
export const setFormSessionTitleColor = (formId, color) =>
    setFormSessionTitleStyle(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_SESSION_TITLE_COLOR,
    );

// Form Session Info
// (String, Object) => Object
const setFormSessionInfoStyle = (formId, info, type = actionTypes.SET_FORM_SESSION_INFO) =>
    setStyleConfigurationProp(type, formId, {
        sessionDetails: {
            info,
        },
    });

// (String, Str) => Object
export const setFormSessionInfoFontSize = (formId, fontSize) =>
    setFormSessionInfoStyle(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_SESSION_INFO_FONTSIZE,
    );

// (String, Str) => Object
export const setFormSessionInfoFont = (formId, font) =>
    setFormSessionInfoStyle(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_SESSION_INFO_FONT,
    );

// (String, Str) => Object
export const setFormSessionInfoColor = (formId, color) =>
    setFormSessionInfoStyle(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_SESSION_INFO_COLOR,
    );

// Form Session Tag
// (String, Object) => Object
const setFormSessionTagStyle = (formId, tag, type = actionTypes.SET_FORM_SESSION_TAG) =>
    setStyleConfigurationProp(type, formId, {
        sessionDetails: {
            tag,
        },
    });

// (String, Str) => Object
export const setFormSessionTagFontSize = (formId, fontSize) =>
    setFormSessionTagStyle(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_SESSION_TAG_FONTSIZE,
    );

// (String, Str) => Object
export const setFormSessionTagFont = (formId, font) =>
    setFormSessionTagStyle(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_SESSION_TAG_FONT,
    );

// (String, Str) => Object
export const setFormSessionTagColor = (formId, color) =>
    setFormSessionTagStyle(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_SESSION_TAG_COLOR,
    );

// (String, Object) => Object
export const setFormSessionTagBackground = (formId, background) =>
    setFormSessionTagStyle(formId, { background }, actionTypes.SET_FORM_SESSION_TAG_BACKGROUND);

// Form Session Tag Border
// (String, Object) => Object
const setFormSessionTagBorder = (formId, border, type = actionTypes.SET_FORM_SESSION_TAG_BORDER) =>
    setStyleConfigurationProp(type, formId, {
        sessionDetails: {
            tag: {
                border,
            },
        },
    });

// (String, Int) => Object
export const setFormSessionTagBorderRadius = (formId, radius) =>
    setFormSessionTagBorder(
        formId,
        {
            radius,
        },
        actionTypes.SET_FORM_SESSION_TAG_BORDER_RADIUS,
    );

// (String, Object) => Object
export const setFormSessionTagBorderSizes = (formId, sizes, type = actionTypes.SET_FORM_SESSION_TAG_BORDER_SIZES) =>
    setFormSessionTagBorder(
        formId,
        {
            sizes,
        },
        type,
    );

// (String, Int) => Object
export const setFormSessionTagBorderSizeTop = (formId, top) =>
    setFormSessionTagBorderSizes(
        formId,
        {
            top,
        },
        actionTypes.SET_FORM_SESSION_TAG_BORDER_SIZE_TOP,
    );

// (String, Int) => Object
export const setFormSessionTagBorderSizeBottom = (formId, bottom) =>
    setFormSessionTagBorderSizes(
        formId,
        {
            bottom,
        },
        actionTypes.SET_FORM_SESSION_TAG_BORDER_SIZE_BOTTOM,
    );

// (String, Int) => Object
export const setFormSessionTagBorderSizeRight = (formId, right) =>
    setFormSessionTagBorderSizes(
        formId,
        {
            right,
        },
        actionTypes.SET_FORM_SESSION_TAG_BORDER_SIZE_RIGHT,
    );

// (String, Int) => Object
export const setFormSessionTagBorderSizeLeft = (formId, left) =>
    setFormSessionTagBorderSizes(
        formId,
        {
            left,
        },
        actionTypes.SET_FORM_SESSION_TAG_BORDER_SIZE_LEFT,
    );

// (String, Int) => Thunk
export const setAllFormSessionTagBorderSizes = (formId, size) => (dispatch) => {
    dispatch(setFormSessionTagBorderSizeTop(formId, size));
    dispatch(setFormSessionTagBorderSizeBottom(formId, size));
    dispatch(setFormSessionTagBorderSizeRight(formId, size));
    dispatch(setFormSessionTagBorderSizeLeft(formId, size));
};

// (String, Int) => Object
export const setFormSessionTagBorderColor = (formId, color) =>
    setFormSessionTagBorder(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_SESSION_TAG_BORDER_COLOR,
    );

// Form Session Badge
// (String, Object) => Object
const setFormSessionBadgeStyle = (formId, badge, type = actionTypes.SET_FORM_SESSION_BADGE) =>
    setStyleConfigurationProp(type, formId, {
        sessionDetails: {
            badge,
        },
    });

// (String, Str) => Object
export const setFormSessionBadgeFontSize = (formId, fontSize) =>
    setFormSessionBadgeStyle(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_SESSION_BADGE_FONTSIZE,
    );

// (String, Str) => Object
export const setFormSessionBadgeFont = (formId, font) =>
    setFormSessionBadgeStyle(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_SESSION_BADGE_FONT,
    );

// (String, Str) => Object
export const setFormSessionBadgeColor = (formId, color) =>
    setFormSessionBadgeStyle(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_SESSION_BADGE_COLOR,
    );

// (String, Object) => Object
export const setFormSessionBadgeBackground = (formId, background) =>
    setFormSessionBadgeStyle(formId, { background }, actionTypes.SET_FORM_SESSION_BADGE_BACKGROUND);

// Form Session Badge Border
// (String, Object) => Object
const setFormSessionBadgeBorder = (formId, border, type = actionTypes.SET_FORM_SESSION_BADGE_BORDER) =>
    setStyleConfigurationProp(type, formId, {
        sessionDetails: {
            badge: {
                border,
            },
        },
    });

// (String, Int) => Object
export const setFormSessionBadgeBorderRadius = (formId, radius) =>
    setFormSessionBadgeBorder(
        formId,
        {
            radius,
        },
        actionTypes.SET_FORM_SESSION_BADGE_BORDER_RADIUS,
    );

// (String, Object) => Object
export const setFormSessionBadgeBorderSizes = (formId, sizes, type = actionTypes.SET_FORM_SESSION_BADGE_BORDER_SIZES) =>
    setFormSessionBadgeBorder(
        formId,
        {
            sizes,
        },
        type,
    );

// (String, Int) => Object
export const setFormSessionBadgeBorderSizeTop = (formId, top) =>
    setFormSessionBadgeBorderSizes(
        formId,
        {
            top,
        },
        actionTypes.SET_FORM_SESSION_BADGE_BORDER_SIZE_TOP,
    );

// (String, Int) => Object
export const setFormSessionBadgeBorderSizeBottom = (formId, bottom) =>
    setFormSessionBadgeBorderSizes(
        formId,
        {
            bottom,
        },
        actionTypes.SET_FORM_SESSION_BADGE_BORDER_SIZE_BOTTOM,
    );

// (String, Int) => Object
export const setFormSessionBadgeBorderSizeLeft = (formId, left) =>
    setFormSessionBadgeBorderSizes(
        formId,
        {
            left,
        },
        actionTypes.SET_FORM_SESSION_BADGE_BORDER_SIZE_LEFT,
    );

// (String, Int) => Object
export const setFormSessionBadgeBorderSizeRight = (formId, right) =>
    setFormSessionBadgeBorderSizes(
        formId,
        {
            right,
        },
        actionTypes.SET_FORM_SESSION_BADGE_BORDER_SIZE_RIGHT,
    );

// (String, Int) => Thunk
export const setAllFormSessionBadgeBorderSizes = (formId, size) => (dispatch) => {
    dispatch(setFormSessionBadgeBorderSizeTop(formId, size));
    dispatch(setFormSessionBadgeBorderSizeBottom(formId, size));
    dispatch(setFormSessionBadgeBorderSizeLeft(formId, size));
    dispatch(setFormSessionBadgeBorderSizeRight(formId, size));
};

// (String, Int) => Object
export const setFormSessionBadgeBorderColor = (formId, color) =>
    setFormSessionBadgeBorder(
        formId,
        {
            color,
        },
        actionTypes.SET_FORM_SESSION_BADGE_BORDER_COLOR,
    );

// Secondary Button colors
// (String, Object) => Object
const setFormSecondaryButtonColors = (formId, colors, type = actionTypes.SET_FORM_SECONDARY_BUTTON_COLORS) =>
    setStyleConfigurationProp(type, formId, {
        secondaryButton: {
            colors,
        },
    });

// (String, Int) => Object
export const setFormSecondaryButtonColorDefault = (formId, defaultColor) =>
    setFormSecondaryButtonColors(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormSecondaryButtonColorHover = (formId, hover) =>
    setFormSecondaryButtonColors(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_COLOR_HOVER,
    );

// Secondary Button colors
// (String, Object) => Object
const setFormSecondaryButtonText = (formId, text, type = actionTypes.SET_FORM_SECONDARY_BUTTON_TEXT) =>
    setStyleConfigurationProp(type, formId, {
        secondaryButton: {
            text,
        },
    });

// (String, Int) => Object
export const setFormSecondaryButtonTextFont = (formId, font) =>
    setFormSecondaryButtonText(
        formId,
        {
            font,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_TEXT_FONT,
    );

// (String, Int) => Object
export const setFormSecondaryButtonTextFontSize = (formId, fontSize) =>
    setFormSecondaryButtonText(
        formId,
        {
            fontSize,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_TEXT_FONTSIZE,
    );

// (String, Object) => Object
const setFormSecondaryButtonTextColors = (formId, colors, type = actionTypes.SET_FORM_SECONDARY_BUTTON_TEXT_COLORS) =>
    setFormSecondaryButtonText(
        formId,
        {
            colors,
        },
        type,
    );

// (String, Int) => Object
export const setFormSecondaryButtonTextColorDefault = (formId, defaultColor) =>
    setFormSecondaryButtonTextColors(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_TEXT_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormSecondaryButtonTextColorHover = (formId, hover) =>
    setFormSecondaryButtonTextColors(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_TEXT_COLOR_HOVER,
    );

// Secondary Button border
// (String, Object) => Object
const setFormSecondaryButtonBorder = (formId, border, type = actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER) =>
    setStyleConfigurationProp(type, formId, {
        secondaryButton: {
            border,
        },
    });

// (String, Int) => Object
export const setFormSecondaryButtonBorderRadius = (formId, radius) =>
    setFormSecondaryButtonBorder(
        formId,
        {
            radius,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_RADIUS,
    );

// (String, Object) => Object
export const setFormSecondaryButtonBorderSizes = (
    formId,
    sizes,
    type = actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_SIZES,
) =>
    setFormSecondaryButtonBorder(
        formId,
        {
            sizes,
        },
        type,
    );

// (String, Int) => Object
export const setFormSecondaryButtonBorderSizeTop = (formId, top) =>
    setFormSecondaryButtonBorderSizes(
        formId,
        {
            top,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_SIZE_TOP,
    );

// (String, Int) => Object
export const setFormSecondaryButtonBorderSizeBottom = (formId, bottom) =>
    setFormSecondaryButtonBorderSizes(
        formId,
        {
            bottom,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_SIZE_BOTTOM,
    );

// (String, Int) => Object
export const setFormSecondaryButtonBorderSizeRight = (formId, right) =>
    setFormSecondaryButtonBorderSizes(
        formId,
        {
            right,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_SIZE_RIGHT,
    );

// (String, Int) => Object
export const setFormSecondaryButtonBorderSizeLeft = (formId, left) =>
    setFormSecondaryButtonBorderSizes(
        formId,
        {
            left,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_SIZE_LEFT,
    );

// (String, Int) => Thunk
export const setAllFormSecondaryButtonBorderSizes = (formId, size) => (dispatch) => {
    dispatch(setFormSecondaryButtonBorderSizeTop(formId, size));
    dispatch(setFormSecondaryButtonBorderSizeBottom(formId, size));
    dispatch(setFormSecondaryButtonBorderSizeRight(formId, size));
    dispatch(setFormSecondaryButtonBorderSizeLeft(formId, size));
};

// (String, Object) => Object
export const setFormSecondaryButtonBorderColors = (
    formId,
    colors,
    type = actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_COLORS,
) =>
    setFormSecondaryButtonBorder(
        formId,
        {
            colors,
        },
        type,
    );

// (String, Int) => Object
export const setFormSecondaryButtonBorderColorDefault = (formId, defaultColor) =>
    setFormSecondaryButtonBorderColors(
        formId,
        {
            default: defaultColor,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_COLOR_DEFAULT,
    );

// (String, Int) => Object
export const setFormSecondaryButtonBorderColorHover = (formId, hover) =>
    setFormSecondaryButtonBorderColors(
        formId,
        {
            hover,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_COLOR_HOVER,
    );

export const setFormSecondaryButtonBorderColorFocus = (formId, focus) =>
    setFormSecondaryButtonBorderColors(
        formId,
        {
            focus,
        },
        actionTypes.SET_FORM_SECONDARY_BUTTON_BORDER_COLOR_FOCUS,
    );

// ////////////////////////////////////////////////////////////////////////////////////
// Ticket Styles
/**
 * Maps the ticket updated styles to it's corresponding category.
 * @param {Array} ticketStyles [{ type: standalone, ...restStyles}, { type: nested, ...restStyles }]
 * @param {string} category
 * @param {Object} updatedTicketCategoryStyles { ticketName: { font: 'Lato' } }
 */
const mapNewTicketStylesByCategory = (ticketStyles, category, updatedTicketCategoryStyles) =>
    ticketStyles.map((ticketStyle) => (ticketStyle.type === category ? updatedTicketCategoryStyles : ticketStyle));

/**
 * Receives a category and it's new ticketStyles to create an updated version of the existing
 * ones, or create them from scratch (using Master ticket styles as a base for this).
 * Then updates the whole tickets styles array.
 * @param {string} formId
 * @param {string} category
 * @param {Object} newTicketStyles
 */
export const setTicketStyleConfigurationProp = (formId, category, newTicketStyles) => (dispatch, getState) => {
    const state = getState();
    const ticketStyles = getTicketStyles(state, formId);
    const ticketStyleCategories = getTicketStyleCategories(state, formId);
    const isCategoryDefined = ticketStyleCategories.includes(category);

    const updatedTicketCategoryStyles = {
        ...(isCategoryDefined
            ? getTicketStylesByCategory(state, formId, category)
            : {
                  ...getTicketStylesByCategory(state, formId, TICKET_TYPE_STANDALONE),
                  type: category,
              }),
        ...newTicketStyles,
        lastEdited: Date.now(),
    };

    const updatedTicketStyles = isCategoryDefined
        ? mapNewTicketStylesByCategory(ticketStyles, category, updatedTicketCategoryStyles)
        : [...ticketStyles, updatedTicketCategoryStyles];

    dispatch(
        setStyleConfigurationProp(actionTypes.SET_TICKET_STYLE_CONFIGURATION, formId, {
            tickets: updatedTicketStyles,
        }),
    );
};

// Ticket Name
const setTicketNameText = (formId, ticketName, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketName });

export const setTicketNameTextFont = (formId, font, category) =>
    setTicketNameText(
        formId,
        {
            font,
        },
        category,
    );

export const setTicketNameTextFontsize = (formId, fontSize, category) =>
    setTicketNameText(
        formId,
        {
            fontSize,
        },
        category,
    );

export const setTicketNameTextColor = (formId, color, category) =>
    setTicketNameText(
        formId,
        {
            color,
        },
        category,
    );

// Ticket Description
const setTicketDescriptionText = (formId, ticketDescription, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketDescription });

export const setTicketDescriptionTextFont = (formId, font, category) =>
    setTicketDescriptionText(
        formId,
        {
            font,
        },
        category,
    );

export const setTicketDescriptionTextFontsize = (formId, fontSize, category) =>
    setTicketDescriptionText(
        formId,
        {
            fontSize,
        },
        category,
    );

export const setTicketDescriptionTextColor = (formId, color, category) =>
    setTicketDescriptionText(
        formId,
        {
            color,
        },
        category,
    );

// Ticket Remaining
const setTicketRemainingText = (formId, ticketRemaining, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketRemaining });

export const setTicketRemainingTextFont = (formId, font, category) =>
    setTicketRemainingText(
        formId,
        {
            font,
        },
        category,
    );

export const setTicketRemainingTextFontsize = (formId, fontSize, category) =>
    setTicketRemainingText(
        formId,
        {
            fontSize,
        },
        category,
    );

export const setTicketRemainingTextColor = (formId, color, category) =>
    setTicketRemainingText(
        formId,
        {
            color,
        },
        category,
    );

// Ticket Date and Time
const setTicketDateTimeText = (formId, ticketDateTime, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketDateTime });

export const setTicketDateTimeTextFont = (formId, font, category) =>
    setTicketDateTimeText(
        formId,
        {
            font,
        },
        category,
    );

export const setTicketDateTimeTextFontsize = (formId, fontSize, category) =>
    setTicketDateTimeText(
        formId,
        {
            fontSize,
        },
        category,
    );

export const setTicketDateTimeTextColor = (formId, color, category) =>
    setTicketDateTimeText(
        formId,
        {
            color,
        },
        category,
    );

// Ticket Price
const setTicketPriceText = (formId, ticketPrice, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketPrice });

export const setTicketPriceTextFont = (formId, font, category) =>
    setTicketPriceText(
        formId,
        {
            font,
        },
        category,
    );

export const setTicketPriceTextFontsize = (formId, fontSize, category) =>
    setTicketPriceText(
        formId,
        {
            fontSize,
        },
        category,
    );

export const setTicketPriceTextColor = (formId, color, category) =>
    setTicketPriceText(
        formId,
        {
            color,
        },
        category,
    );

// Ticket style
export const setTicketStylesLayout = (formId, ticketStyle, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketStyle });

// Ticket background color and image
const setTicketBackgroundStyle = (formId, ticketBackground, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketBackground });

export const setTicketBackgroundColor = (formId, color, category) =>
    setTicketBackgroundStyle(
        formId,
        {
            color,
        },
        category,
    );

export const setTicketBackgroundImageBaseUrl = (formId, baseUrl, category) =>
    setTicketBackgroundStyle(
        formId,
        {
            baseUrl,
        },
        category,
    );

export const setTicketBackgroundImageUrl = (formId, url, category) =>
    setTicketBackgroundStyle(
        formId,
        {
            url,
        },
        category,
    );

export const setTicketBackgroundOpacity = (formId, opacity, category) =>
    setTicketBackgroundStyle(
        formId,
        {
            opacity,
        },
        category,
    );

// Ticket border styles
const setTicketBorder = (formId, ticketBorder, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketBorder });

export const setTicketBorderRadius = (formId, radius, category) =>
    setTicketBorder(
        formId,
        {
            radius,
        },
        category,
    );

export const setTicketBorderSizes = (formId, sizes, category) =>
    setTicketBorder(
        formId,
        {
            sizes,
        },
        category,
    );

export const setTicketBorderSizeTop = (formId, top, category) =>
    setTicketBorder(
        formId,
        {
            sizes: {
                top,
            },
        },
        category,
    );

export const setTicketBorderSizeBottom = (formId, bottom, category) =>
    setTicketBorder(
        formId,
        {
            sizes: {
                bottom,
            },
        },
        category,
    );

export const setTicketBorderSizeLeft = (formId, left, category) =>
    setTicketBorder(
        formId,
        {
            sizes: {
                left,
            },
        },
        category,
    );

export const setTicketBorderSizeRight = (formId, right, category) =>
    setTicketBorder(
        formId,
        {
            sizes: {
                right,
            },
        },
        category,
    );

export const setTicketBorderWeight = (formId, weight, category) => (dispatch) => {
    dispatch(setTicketBorderSizeTop(formId, weight, category));
    dispatch(setTicketBorderSizeBottom(formId, weight, category));
    dispatch(setTicketBorderSizeLeft(formId, weight, category));
    dispatch(setTicketBorderSizeRight(formId, weight, category));
};

export const setTicketBorderColors = (formId, colors, category) =>
    setTicketBorder(
        formId,
        {
            colors,
        },
        category,
    );

export const setTicketBorderColorDefault = (formId, defaultColor, category) =>
    setTicketBorderColors(
        formId,
        {
            default: defaultColor,
        },
        category,
    );

export const setTicketBorderColorHover = (formId, hover, category) =>
    setTicketBorderColors(
        formId,
        {
            hover,
        },
        category,
    );

// Ticket overlay color and image
const setTicketOverlayStyle = (formId, ticketOverlay, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketOverlay });

export const setTicketOverlayColor = (formId, color, category) =>
    setTicketOverlayStyle(
        formId,
        {
            color,
        },
        category,
    );

export const setTicketOverlayImageBaseUrl = (formId, baseUrl, category) =>
    setTicketOverlayStyle(
        formId,
        {
            baseUrl,
        },
        category,
    );

export const setTicketOverlayImageUrl = (formId, url, category) =>
    setTicketOverlayStyle(
        formId,
        {
            url,
        },
        category,
    );

export const setTicketOverlayOpacity = (formId, opacity, category) =>
    setTicketOverlayStyle(
        formId,
        {
            opacity,
        },
        category,
    );

// Ticket Sold Out styles
const setTicketVisibility = (formId, soldOutVisibility, category) =>
    setTicketStyleConfigurationProp(formId, category, { soldOutVisibility });

export const setTicketSoldOutVisibility = (formId, soldOutVisibility, category) =>
    setTicketVisibility(formId, soldOutVisibility, category);

// Ticket Quantity styles
const setTicketQuantityStyle = (formId, ticketQuantity, category) =>
    setTicketStyleConfigurationProp(formId, category, { ticketQuantity });

export const setTicketQuantityInputType = (formId, inputType, category) =>
    setTicketQuantityStyle(
        formId,
        {
            inputType,
        },
        category,
    );

export const setTicketQuantityBorderRadius = (formId, radius, category) =>
    setTicketQuantityStyle(
        formId,
        {
            radius,
        },
        category,
    );

export const setTicketQuantityBorderSizes = (formId, sizes, category) =>
    setTicketQuantityStyle(
        formId,
        {
            sizes,
        },
        category,
    );

export const setTicketQuantityBorderSizeTop = (formId, top, category) =>
    setTicketQuantityStyle(
        formId,
        {
            sizes: {
                top,
            },
        },
        category,
    );

export const setTicketQuantityBorderSizeBottom = (formId, bottom, category) =>
    setTicketQuantityStyle(
        formId,
        {
            sizes: {
                bottom,
            },
        },
        category,
    );

export const setTicketQuantityBorderSizeLeft = (formId, left, category) =>
    setTicketQuantityStyle(
        formId,
        {
            sizes: {
                left,
            },
        },
        category,
    );

export const setTicketQuantityBorderSizeRight = (formId, right, category) =>
    setTicketQuantityStyle(
        formId,
        {
            sizes: {
                right,
            },
        },
        category,
    );

export const setTicketQuantityBorderWeight = (formId, weight, category) => (dispatch) => {
    dispatch(setTicketQuantityBorderSizeTop(formId, weight, category));
    dispatch(setTicketQuantityBorderSizeBottom(formId, weight, category));
    dispatch(setTicketQuantityBorderSizeLeft(formId, weight, category));
    dispatch(setTicketQuantityBorderSizeRight(formId, weight, category));
};

export const setTicketQuantityBorderColors = (formId, colors, category) =>
    setTicketQuantityStyle(
        formId,
        {
            colors,
        },
        category,
    );

export const setTicketQuantityBorderColorDefault = (formId, defaultColor, category) =>
    setTicketQuantityBorderColors(
        formId,
        {
            default: defaultColor,
        },
        category,
    );

export const setTicketQuantityBorderColorHover = (formId, hover, category) =>
    setTicketQuantityBorderColors(
        formId,
        {
            hover,
        },
        category,
    );

// (String, Int) => Object
export const setTicketQuantityInputFont = (formId, font, category) =>
    setTicketQuantityStyle(
        formId,
        {
            font,
        },
        category,
    );

// (String, Int) => Object
export const setTicketQuantityInputFontSize = (formId, fontSize, category) =>
    setTicketQuantityStyle(
        formId,
        {
            fontSize,
        },
        category,
    );

// (String, Int) => Object
export const setTicketQuantityInputAnswerColor = (formId, color, category) =>
    setTicketQuantityStyle(
        formId,
        {
            fontColors: {
                answer: color,
            },
        },
        category,
    );

// (String, Int) => Object
export const setTicketQuantityInputPlaceholderColor = (formId, color, category) =>
    setTicketQuantityStyle(
        formId,
        {
            fontColors: {
                placeholder: color,
            },
        },
        category,
    );

// (String, Int) => Object
export const setTicketQuantityInputBackgroundColorDefault = (formId, defaultColor, category) =>
    setTicketQuantityStyle(
        formId,
        {
            inputBackgroundColors: {
                default: defaultColor,
            },
        },
        category,
    );

// (String, Int) => Object
export const setTicketQuantityInputBackgroundColorHover = (formId, hoverColor, category) =>
    setTicketQuantityStyle(
        formId,
        {
            inputBackgroundColors: {
                hover: hoverColor,
            },
        },
        category,
    );
